/**
 * Unified Environment Configuration for CoreArena Frontend
 * 
 * This replaces the hardcoded environment.js with a proper environment-based configuration
 * that reads from process.env (React environment variables)
 */

// Environment detection
const NODE_ENV = process.env.NODE_ENV || 'development';
const isProduction = NODE_ENV === 'production' || process.env.REACT_APP_FORCE_PRODUCTION === 'true'; // Removed forced production mode
const isDevelopment = !isProduction;

console.log('🔧 [ENV] Environment Detection:', {
    NODE_ENV,
    REACT_APP_FORCE_PRODUCTION: process.env.REACT_APP_FORCE_PRODUCTION,
    isProduction,
    REACT_APP_CHAT_URL: process.env.REACT_APP_CHAT_URL
});

// Base URLs - from environment variables with fallbacks
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (isProduction ? 'https://corearena.tv' : 'http://localhost:9200');
const HLS_BASE_URL = process.env.REACT_APP_HLS_BASE_URL || (isProduction ? 'https://ivs.eu-west-1.amazonaws.com' : 'http://localhost:8080');

// Chat URLs - Force empty string for same-origin in production (no port!)
const CHAT_URL = isProduction ? '' : (process.env.REACT_APP_CHAT_URL || 'http://localhost:3333');
const WS_URL = isProduction ? '' : (process.env.REACT_APP_WS_URL || 'ws://localhost:3333');

console.log('🔧 [ENV] Final Chat Configuration:', {
    CHAT_URL,
    WS_URL,
    isProduction
});

// reCAPTCHA configuration
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LerW1QrAAAAAJl7I1-tRlgKrgbzy1IYUR7h3n0U';
const ENABLE_RECAPTCHA = process.env.REACT_APP_ENABLE_RECAPTCHA === 'true' || isProduction;

// Translation Service configuration (backend proxy)
// IMPORTANT: Always go through gateway, not directly to translation service
const TRANSLATION_API_URL = process.env.REACT_APP_TRANSLATION_API_URL || (isProduction ? 'https://corearena.tv/api/translate' : 'http://localhost:9200/api/translate');
const DEFAULT_LANGUAGE = process.env.REACT_APP_DEFAULT_LANGUAGE || 'fr';

// Debug logs - Check specific variables causing undefined
console.log('🔧 [FRONTEND] Checking undefined variables:', {
    'REACT_APP_HOMEPAGE': process.env.REACT_APP_HOMEPAGE,
    'REACT_APP_LOGOUT_ENDPOINT': process.env.REACT_APP_LOGOUT_ENDPOINT,
    'REACT_APP_API_BASE': process.env.REACT_APP_API_BASE,
    'REACT_APP_API_BASE_URL': process.env.REACT_APP_API_BASE_URL,
    'FINAL_API_BASE_URL': API_BASE_URL
});

// Export unified configuration
export const ENV_CONFIG = {
    // Environment flags
    NODE_ENV,
    isProduction,
    isDevelopment,
    
    // Base URLs
    API_BASE_URL,
    HLS_BASE_URL,
    HOMEPAGE: isProduction ? 'https://corearena.tv' : 'http://localhost:3000',
    
    // Auth Service Endpoints
    AUTH_ENDPOINT: `${API_BASE_URL}/api/auth`,
    LOGIN_ENDPOINT: `${API_BASE_URL}/api/auth/login`,
    SIGNUP_ENDPOINT: `${API_BASE_URL}/api/auth/signup`,
    LOGOUT_ENDPOINT: `${API_BASE_URL}/api/auth/logout`,
    OUATH_GOOGLE_ENDPOINT: `${API_BASE_URL}/api/auth/oauth/google`,
    OUATH_FACEBOOK_ENDPOINT: `${API_BASE_URL}/api/auth/oauth/facebook`,
    AUTH_RESET_PASSWORD_ENDPOINT: `${API_BASE_URL}/api/auth/reset`,
    AUTH_NEW_PASSWORD_ENDPOINT: `${API_BASE_URL}/api/auth/reset/`,
    DATA_ENDPOINT: `${API_BASE_URL}/api/user/`,
    
    // Stream Service Endpoints
    STREAMS_ENDPOINT: `${API_BASE_URL}/api/stream/streams`,
    START_STREAM_ENDPOINT: `${API_BASE_URL}/api/stream/startstreaming`,
    STREAM_ENDPOINT: `${API_BASE_URL}/api/stream/stream/`,
    MYSTREAMKEY_ENDPOINT: `${API_BASE_URL}/api/stream/mystreamkey`,
    INIT_CHANNEL_ENDPOINT: `${API_BASE_URL}/api/stream/mychannel/init`,
    CHANNELS_SETTINGS: `${API_BASE_URL}/api/stream/mychannelinfo`,
    CHANNELS_SETTINGS_UPDATE: `${API_BASE_URL}/api/auth/update/`,
    CHANNELS_SETTINGS_UPDATE_USERNAME: `${API_BASE_URL}/api/auth/username`,
    CHANNELS_SETTINGS_UPDATE_FULLNAME: `${API_BASE_URL}/api/auth/fullname`,
    CHANNELS_SETTINGS_UPDATE_PASSWORD: `${API_BASE_URL}/api/auth/password`,
    AVATAR_ENDPOINT: `${API_BASE_URL}/api/auth/avatar`,
    BANNER_ENDPOINT: `${API_BASE_URL}/api/auth/banner`,
    SEARCH_ENDPOINT: `${API_BASE_URL}/api/stream/search?term=`,
    SEARCH_GAME_ENDPOINT: `${API_BASE_URL}/api/stream/search?term=`,
    GET_GAME_ENDPOINT: `${API_BASE_URL}/api/stream/games`,
    STREAM_VIEWERS_ENDPOINT: `${API_BASE_URL}/api/stream/livedata/`,
    FOLLOW_ENDPOINT: `${API_BASE_URL}/api/stream/follow`,
    UNFOLLOW_ENDPOINT: `${API_BASE_URL}/api/stream/unfollow`,
    FOLLOWERS_ENDPOINT: `${API_BASE_URL}/api/stream/myfollowers?page=`,
    
    // Tournament Service Endpoints
    TOURNAMENT_ENDPOINT: `${API_BASE_URL}/api/tournament`,
    CREATE_TOURNAMENT_ENDPOINT: `${API_BASE_URL}/api/tournament/create_tournament`,
    JOIN_TOURNAMENT_ENDPOINT: `${API_BASE_URL}/api/tournament/player/join_tournament`,
    LEAVE_TOURNAMENT_ENDPOINT: `${API_BASE_URL}/api/tournament/player/unjoin`,
    TOURNAMENT_MY_TOURNAMENTS_ENDPOINT: `${API_BASE_URL}/api/tournament/my-tournaments`,
    TOURNAMENT_GET_TOURNAMENTS_ENDPOINT: `${API_BASE_URL}/api/tournament/get`,
    TOURNAMENT_IS_ADMIN_ENDPOINT: `${API_BASE_URL}/api/tournament/isAdmin/`,
    // ms_tournament_service route is POST /tournament/startTournament (proxied under /api/tournament/startTournament)
    TOURNAMENT_START_ENDPOINT: `${API_BASE_URL}/api/tournament/startTournament`,
    // Tournament players (ms_tournament_service has prefix '/player')
    GET_PLAYERS_ENDPOINT: `${API_BASE_URL}/api/tournament/player/getPlayers/`,
    APPROVE_PLAYER_ENDPOINT: `${API_BASE_URL}/api/tournament/player/approvePlayer`,
    PLAYER_ENDPOINT: `${API_BASE_URL}/api/tournament/player/`,
    // ms_tournament_service route is POST /tournament/setWinner (proxied under /api/tournament/setWinner)
    SET_WINNER_ENDPOINT: `${API_BASE_URL}/api/tournament/setWinner`,
    UPDATE_TOURNAMENT_ENDPOINT: `${API_BASE_URL}/api/tournament/update/`,
    GAME_ENDPOINT: `${API_BASE_URL}/api/stream/streamgames/`,
    
    // Event Service Endpoints
    EVENTS_ENDPOINT: `${API_BASE_URL}/api/events`,
    EVENT_CATEGORIES_ENDPOINT: `${API_BASE_URL}/api/events/categories`,
    EVENT_PARTICIPANTS_ENDPOINT: `${API_BASE_URL}/api/events/participants`,
    
    // Wallet Service Endpoints
    WALLET_BALANCE_ENDPOINT: `${API_BASE_URL}/api/wallet/balance`,
    WALLET_DEBIT_ENDPOINT: `${API_BASE_URL}/api/wallet/debit`,
    WALLET_CREDIT_ENDPOINT: `${API_BASE_URL}/api/wallet/credit`,
    WALLET_TRANSACTIONS_ENDPOINT: `${API_BASE_URL}/api/wallet/transactions`,
    WALLET_TOTAL_GCOINS_SOLD_ENDPOINT: `${API_BASE_URL}/api/wallet/total-gcoins-sold`,
    
  // Ticket Service Endpoints
  TICKET_PURCHASE_ENDPOINT: `${API_BASE_URL}/api/tickets/purchase`,
  TICKET_USER_ENDPOINT: `${API_BASE_URL}/api/tickets/user`,
  TICKET_VALIDATE_ENDPOINT: `${API_BASE_URL}/api/tickets/validate`,
  TICKET_EVENT_ENDPOINT: `${API_BASE_URL}/api/tickets/event`,
  TICKET_REDEEM_ENDPOINT: `${API_BASE_URL}/api/tickets/redeem`,
  TICKET_GENERATE_ENDPOINT: `${API_BASE_URL}/api/tickets/event`,
  TICKET_CODES_ENDPOINT: `${API_BASE_URL}/api/tickets/event`,

  // Assignment Service Endpoints (Event-Streamer Assignments)
  ASSIGNMENT_CREATE_ENDPOINT: `${API_BASE_URL}/api/assignments`,
  ASSIGNMENT_EVENT_ENDPOINT: `${API_BASE_URL}/api/assignments/event`,
  ASSIGNMENT_CHECK_ENDPOINT: `${API_BASE_URL}/api/assignments/check`,
  ASSIGNMENT_STREAMER_ENDPOINT: `${API_BASE_URL}/api/assignments/streamer`,
    
    // Legal Service Endpoints
    LEGAL_RGPD_ENDPOINT: `${API_BASE_URL}/api/legal/rgpd`,
    LEGAL_CGU_ENDPOINT: `${API_BASE_URL}/api/legal/cgu`,
    LEGAL_FAQ_ENDPOINT: `${API_BASE_URL}/api/legal/faq`,
    LEGAL_MENTIONS_ENDPOINT: `${API_BASE_URL}/api/legal/mentions-legales`,
    LEGAL_ALL_ENDPOINT: `${API_BASE_URL}/api/legal/all`,
    
    // Games
    GAMES_ENDPOINT: `${API_BASE_URL}/api/stream/games`,
    
    // Chat Service
    CHAT_ENDPOINT: CHAT_URL,
    CHAT_WS_ENDPOINT: WS_URL,
    CHAT_API_ENDPOINT: isProduction ? '/api/messages' : `${CHAT_URL}/api/messages`,
    
    // Recordings/Replay Service
    RECORDINGS_BASE_URL: process.env.REACT_APP_RECORDINGS_BASE_URL || (isProduction ? 'https://corearena.tv' : 'http://localhost:8080'),
    
    // reCAPTCHA
    REACPTCHA_SITEKEY: RECAPTCHA_SITE_KEY,
    ENABLE_RECAPTCHA,
    
    // Translation Service
    TRANSLATION_API_URL,
    DEFAULT_LANGUAGE
};

// Helper function to get environment variable
export const getEnvVar = (key) => {
    return ENV_CONFIG[key] || process.env[key];
};

export default ENV_CONFIG;
